import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import PriceList from '../components/PriceList';
import SEO from '../components/SEO';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function Prices() {
  const [prices, setPrices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadPrices() {
      const data = await fetchCollection('price_items');
      if (mounted) {
        setPrices(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        setLoading(false);
      }
    }

    loadPrices();
    return () => {
      mounted = false;
    };
  }, []);

  const categories = [
    { id: 'all', label: 'Alle behandlinger' },
    { id: 'Fedtfrysning', label: 'Fedtfrysning' },
    { id: 'EMS Træning', label: 'EMS Træning' },
    { id: 'Body Roll', label: 'Body Roll' },
    { id: 'VIP Forløb', label: 'VIP Forløb' },
  ];

  return (
    <div className="animate-in fade-in duration-200">
      <SEO preset="prices" />
      {/* 1. HERO */}
      <section className="py-16 sm:py-20 bg-sand-light/60 border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-xs text-taupe mb-6" aria-label="Brødkrummesti">
            <Link to="/" className="hover:text-espresso transition-colors">
              Forside
            </Link>
            <span className="text-taupe-border">/</span>
            <span className="text-espresso font-medium">Priser</span>
          </nav>

          <h1 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso uppercase tracking-wider">
            Prisoversigt & Tilbud
          </h1>

          <p className="text-base sm:text-lg text-taupe mt-4 leading-relaxed">
            Se oversigten over vores behandlinger, klippekort og aktuelle tilbud hos{' '}
            <Link to="/" className="text-espresso font-semibold hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/50 hover:decoration-accent-brown transition-colors">
              Cryo by Breum
            </Link>
            . Alle behandlinger bookes direkte via klinikkens officielle Planway booking.
          </p>

          {/* Hurtige links til behandlingsundersider */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-6 text-xs font-semibold uppercase tracking-wider text-espresso">
            <span className="text-taupe font-normal lowercase tracking-normal">Læs om:</span>
            <Link
              to="/behandlinger/fedtfrysning"
              className="px-3 py-1.5 bg-white border border-taupe-border/80 hover:border-espresso hover:text-accent-brown transition-colors"
            >
              Fedtfrysning
            </Link>
            <Link
              to="/behandlinger/ems-traening"
              className="px-3 py-1.5 bg-white border border-taupe-border/80 hover:border-espresso hover:text-accent-brown transition-colors"
            >
              EMS Træning
            </Link>
            <Link
              to="/behandlinger/body-roll"
              className="px-3 py-1.5 bg-white border border-taupe-border/80 hover:border-espresso hover:text-accent-brown transition-colors"
            >
              Body Roll
            </Link>
          </div>

          <div className="pt-6">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-colors shadow-xs"
            >
              <span>Åbn Planway booking</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* 2. FILTER & PRICE LIST */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-16">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-5 py-2.5 text-xs font-semibold tracking-wider uppercase transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-espresso text-white shadow-xs'
                  : 'bg-sand hover:bg-sand-dark text-espresso border border-taupe-border/60'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center space-y-3">
            <i className="fa-solid fa-circle-notch fa-spin text-2xl text-taupe" aria-hidden="true" />
            <p className="text-sm text-taupe">Henter aktuelle priser...</p>
          </div>
        ) : (
          <PriceList
            items={prices}
            filterCategory={selectedCategory === 'all' ? null : selectedCategory}
          />
        )}
      </section>

      {/* 3. PRACTICAL INFO & VIP */}
      <section className="py-20 bg-white border-y border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 reveal-on-scroll">
            <div className="p-8 border border-taupe-border/80 space-y-3">
              <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                Information om tilbud
              </h3>
              <p className="text-sm text-taupe leading-relaxed">
                Tilbud gælder i de anførte perioder eller for et begrænset antal gange per kunde (eksempelvis må opstartstræningstilbuddet kun benyttes én gang per person).
              </p>
            </div>

            <div className="p-8 border border-taupe-border/80 space-y-3">
              <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                Skræddersyede VIP-forløb
              </h3>
              <p className="text-sm text-taupe leading-relaxed">
                Ønsker du et skræddersyet forløb med 10 behandlinger eller en kombination af{' '}
                <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  Fedtfrysning
                </Link>
                ,{' '}
                <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  EMS
                </Link>{' '}
                og{' '}
                <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  Body Roll
                </Link>
                ? Kontakt os direkte via vores{' '}
                <Link to="/kontakt" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  kontaktside
                </Link>{' '}
                eller ring på tlf.{' '}
                <a href="tel:+4526434402" className="text-espresso font-semibold hover:underline whitespace-nowrap">
                  26 43 44 02
                </a>{' '}
                for en individuel pris og sammensætning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BOOKING BANNER */}
      <section className="py-20 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 reveal-on-scroll">
          <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
            Klar til at booke din tid?
          </h2>
          <p className="text-taupe text-sm sm:text-base max-w-lg mx-auto">
            Vælg din ønskede behandling og find en ledig tid i kalenderen hos Cryo by Breum på Dalumvej 82 i Odense.
          </p>
          <div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-9 py-4 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-colors shadow-xs"
            >
              <span>Book tid på Planway</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
